import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { base_url } from "../../config/config";

function UploadInvoicePageQR() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = searchParams.get("id");
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResults, setUploadResults] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [completedUploads, setCompletedUploads] = useState(0);
  
  // Close success popup after 3 seconds
  useEffect(() => {
    let timer;
    if (showSuccessPopup) {
      timer = setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showSuccessPopup]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const maxSizeInMB = 500;
      const validFiles = [];
      const invalidFiles = [];
      
      selectedFiles.forEach((selectedFile, index) => {
        const fileSizeInMB = selectedFile.size / (1024 * 1024);
        
        if (fileSizeInMB > maxSizeInMB) {
          invalidFiles.push({
            name: selectedFile.name,
            size: fileSizeInMB.toFixed(2)
          });
        } else {
          validFiles.push({
            file: selectedFile,
            id: Date.now() + index,
            status: 'pending', // pending, uploading, success, error
            progress: 0,
            error: null
          });
        }
      });
      
      if (invalidFiles.length > 0) {
        setMessage(`${invalidFiles.length} Datei(en) sind zu groß: ${invalidFiles.map(f => `${f.name} (${f.size}MB)`).join(', ')}. Maximale Größe ist ${maxSizeInMB}MB.`);
      } else {
        setMessage("");
      }
      
      setFiles(prevFiles => [...prevFiles, ...validFiles]);
    }
  };

  const removeFile = (fileId) => {
    setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
    setUploadResults(prev => {
      const newResults = { ...prev };
      delete newResults[fileId];
      return newResults;
    });
  };

  const uploadFile = (fileData) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("invoices", fileData.file);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(prev => ({
            ...prev,
            [fileData.id]: percentComplete
          }));
        }
      });
      
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadResults(prev => ({
            ...prev,
            [fileData.id]: { success: true, message: "Erfolgreich hochgeladen" }
          }));
          setFiles(prev => prev.map(f => 
            f.id === fileData.id ? { ...f, status: 'success' } : f
          ));
          resolve(fileData);
        } else {
          const errorMessage = `Upload fehlgeschlagen: ${xhr.statusText || 'Unbekannter Fehler'}`;
          setUploadResults(prev => ({
            ...prev,
            [fileData.id]: { success: false, message: errorMessage }
          }));
          setFiles(prev => prev.map(f => 
            f.id === fileData.id ? { ...f, status: 'error', error: errorMessage } : f
          ));
          reject(new Error(errorMessage));
        }
      });
      
      xhr.addEventListener("error", () => {
        const errorMessage = "Netzwerkfehler beim Hochladen";
        setUploadResults(prev => ({
          ...prev,
          [fileData.id]: { success: false, message: errorMessage }
        }));
        setFiles(prev => prev.map(f => 
          f.id === fileData.id ? { ...f, status: 'error', error: errorMessage } : f
        ));
        reject(new Error(errorMessage));
      });
      
      xhr.addEventListener("abort", () => {
        const errorMessage = "Upload abgebrochen";
        setUploadResults(prev => ({
          ...prev,
          [fileData.id]: { success: false, message: errorMessage }
        }));
        setFiles(prev => prev.map(f => 
          f.id === fileData.id ? { ...f, status: 'error', error: errorMessage } : f
        ));
        reject(new Error(errorMessage));
      });
      
      setFiles(prev => prev.map(f => 
        f.id === fileData.id ? { ...f, status: 'uploading' } : f
      ));
      
      xhr.open("POST", `${base_url}/v1/updateInvoiceDocument?id=${invoiceId}`, true);
      xhr.timeout = 300000;
      xhr.send(formData);
    });
  };

  const handleUploadAll = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    
    if (pendingFiles.length === 0) {
      setMessage("Keine Dateien zum Hochladen ausgewählt");
      return;
    }

    if (!invoiceId) {
      setMessage("Rechnungs-ID fehlt");
      return;
    }

    setIsUploading(true);
    setCompletedUploads(0);
    
    const uploadPromises = pendingFiles.map(async (fileData) => {
      try {
        await uploadFile(fileData);
        setCompletedUploads(prev => prev + 1);
        return { success: true, fileId: fileData.id };
      } catch (error) {
        setCompletedUploads(prev => prev + 1);
        return { success: false, fileId: fileData.id, error: error.message };
      }
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;
      
      if (successCount > 0 && errorCount === 0) {
        setMessage(`Alle ${successCount} Dateien erfolgreich hochgeladen!`);
        setShowSuccessPopup(true);
      } else if (successCount > 0 && errorCount > 0) {
        setMessage(`${successCount} Dateien erfolgreich hochgeladen, ${errorCount} fehlgeschlagen.`);
      } else {
        setMessage(`Alle ${errorCount} Uploads fehlgeschlagen.`);
      }
      
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      setMessage("Fehler beim Batch-Upload");
    }
    
    setIsUploading(false);
  };

  const clearAllFiles = () => {
    setFiles([]);
    setUploadProgress({});
    setUploadResults({});
    setMessage("");
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const getOverallProgress = () => {
    if (files.length === 0) return 0;
    const totalProgress = Object.values(uploadProgress).reduce((sum, progress) => sum + progress, 0);
    return Math.round(totalProgress / files.length);
  };

  return (
    <div className="max-w-4xl mx-auto mt-10 p-6 border rounded-lg shadow-lg bg-white">
      <h1 className="text-xl font-semibold mb-4 text-center">
        Rechnungen hochladen (Multi-Upload)
      </h1>
      <p className="text-gray-600 mb-6 text-center">
        Rechnungs-ID: {invoiceId}
      </p>
      
      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${
          message.includes("erfolgreich") 
            ? "bg-green-100 text-green-700" 
            : "bg-red-100 text-red-700"
        }`}>
          {message}
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">
          PDF oder Bilder auswählen (mehrere Dateien möglich)
          <span className="block text-xs text-gray-500 mt-1">
            (Maximale Dateigröße pro Datei: 500 MB)
          </span>
        </label>
        <input 
          type="file" 
          multiple
          onChange={handleFileChange} 
          disabled={isUploading}
          className={`w-full ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-medium">Ausgewählte Dateien ({files.length})</h3>
            <button
              onClick={clearAllFiles}
              disabled={isUploading}
              className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50">
              Alle entfernen
            </button>
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto border rounded p-3">
            {files.map((fileData) => (
              <div key={fileData.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {fileData.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(fileData.file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                  
                  {/* Status Badge */}
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                    fileData.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                    fileData.status === 'uploading' ? 'bg-yellow-100 text-yellow-800' :
                    fileData.status === 'success' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {fileData.status === 'pending' ? 'Wartend' :
                     fileData.status === 'uploading' ? 'Wird hochgeladen...' :
                     fileData.status === 'success' ? 'Erfolgreich' :
                     'Fehler'}
                  </span>
                  
                  {/* Error Message */}
                  {fileData.error && (
                    <p className="text-xs text-red-600 mt-1">{fileData.error}</p>
                  )}
                </div>
                
                {/* Progress Bar */}
                {fileData.status === 'uploading' && uploadProgress[fileData.id] !== undefined && (
                  <div className="w-24 mr-3">
                    <div className="text-xs text-gray-600 text-center mb-1">
                      {uploadProgress[fileData.id]}%
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${uploadProgress[fileData.id]}%` }}>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Remove Button */}
                <button
                  onClick={() => removeFile(fileData.id)}
                  disabled={isUploading && fileData.status === 'uploading'}
                  className="ml-3 text-red-600 hover:text-red-800 disabled:opacity-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overall Progress */}
      {isUploading && files.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
            <span>Gesamtfortschritt</span>
            <span>{completedUploads} von {files.filter(f => f.status !== 'success').length + completedUploads} Dateien</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
              style={{ width: `${(completedUploads / files.filter(f => f.status === 'uploading' || f.status === 'pending').length) * 100}%` }}>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Bitte schließen Sie dieses Fenster nicht während des Uploads.
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => navigate("/rechnungs")}
          disabled={isUploading}
          className={`flex-1 px-4 py-2 rounded border border-gray-300 text-gray-700 ${
            isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          }`}>
          Abbrechen
        </button>
        <button
          onClick={handleUploadAll}
          disabled={files.filter(f => f.status === 'pending').length === 0 || isUploading}
          className={`flex-1 px-4 py-2 rounded ${
            files.filter(f => f.status === 'pending').length === 0 || isUploading
              ? 'bg-gray-400 cursor-not-allowed text-white'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}>
          {isUploading ? 'Hochladen...' : `Alle hochladen (${files.filter(f => f.status === 'pending').length})`}
        </button>
      </div>
      
      <button
        onClick={() => navigate("/rechnungs")}
        className="w-full mt-6 text-sm text-blue-600 hover:text-blue-800">
        Zurück zur Rechnungsübersicht
      </button>
      
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className='fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50'>
          <div className='bg-white rounded-lg p-6 shadow-lg max-w-sm'>
            <div className='flex flex-col items-center'>
              <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4'>
                <svg className='w-10 h-10 text-green-500' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                </svg>
              </div>
              <h3 className='text-xl font-bold text-gray-800 mb-2'>Erfolgreich!</h3>
              <p className='text-gray-600 text-center'>Die Dateien wurden erfolgreich hochgeladen.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UploadInvoicePageQR;